import React from 'react';
import PropTypes from 'prop-types';

import Button from '../Button';
import MultilineText from '../MultilineText';
import ConfirmationHtml from '../ConfirmationHtml';
import { getCheckoutConfirmation } from './helpers';
import FilesList from '../FilesList/FilesList';
import request from '../../services/request';
import { apiUrl } from '../../services/env';
import downloadUrlAsFile from '../../services/downloadUrlAsFile';
import getSellerPortalUrl from '../../lib/getSellerPortalUrl';
import useCheckoutState from '../../hooks/useCheckoutState';
import useAiTarget from '../../hooks/useAiTarget';
import { formatDateTime } from '@repo/checkout/lib/format-date';
import getThemeLabels from '@repo/checkout/lib/getThemeLabels';
import StyledElement from '../StyledElement';
import SuccessCheckmark from '../SuccessCheckmark';

const SubscriptionStatusMessage = ({
  subscription,
  email,
  status,
  checkout,
  seller,
  customer,
  preview,
  isConfirmation,
}) => {
  const { restartPayment, theme } = useCheckoutState();
  const aiTarget = useAiTarget();
  const isDisabled = preview;
  const labels = getThemeLabels(theme);

  switch (status) {
    case 'scheduled':
    case 'trialing':
    case 'active': {
      const { title, message, type } = getCheckoutConfirmation({
        checkout,
        subscription,
        seller,
        email,
      });

      const hasFiles = subscription.files && subscription.files.length > 0;

      return (
        <div
          className="cp_status-wrapper text-center px-4 py-8 w-full"
          {...aiTarget('confirmation')}
        >
          <div className="cp_status-content-wrapper">
            <SuccessCheckmark />
            <h2 className="cp_status-title mb-2 text-[var(--cp_checkout-confirmation-heading-color)] [font-size:var(--cp_product-title-font-size)] [line-height:var(--cp_product-title-line-height)] [font-family:var(--cp_product-title-font-family)] [font-weight:var(--cp_product-title-font-weight)] [font-style:var(--cp_product-title-font-style)]">
              {title}
            </h2>
            <div className="cp_status-content mb-4 text-[var(--cp_checkout-confirmation-text-color)]">
              {type === 'html' ? (
                <ConfirmationHtml html={message} preview={preview} className="cp_status-text" />
              ) : (
                <MultilineText className="cp_status-text">{message}</MultilineText>
              )}
            </div>
          </div>

          {hasFiles && checkout.showFilesList && (
            <div className="mb-8">
              <FilesList.List>
                {subscription.files?.map((file, index) => (
                  <FilesList.Item
                    key={file._id}
                    file={file}
                    isFirst={index === 0}
                    isPreview={preview}
                    onDownloadFile={async () => {
                      const response = await request(
                        apiUrl +
                          `/customers/${customer._id}/files/${file._id}/download?token=${customer.loginToken}`,
                      );

                      if (response?.data?.url) {
                        downloadUrlAsFile(response?.data?.url, file.name);
                      } else {
                        window.alert(
                          'Unable to download file. Please try again or contact support.',
                        );
                      }
                    }}
                  />
                ))}
              </FilesList.List>
            </div>
          )}

          {hasFiles && checkout.showViewDownloadsButton && (
            <div className="mb-4">
              <StyledElement
                elementPath="checkout.form.button"
                as={Button}
                className={'cp_button cp_view-downloads-button whitespace-nowrap leading-snug'}
                size="full"
                appearance="submit"
                disabled={isDisabled}
                to={
                  isDisabled
                    ? null
                    : `${getSellerPortalUrl(seller)}/${customer.loginToken}/subscriptions/${subscription._id}`
                }
                target="_blank"
                ignoreBasename
              >
                {labels.viewDownloadsText}
              </StyledElement>
            </div>
          )}
        </div>
      );
    }
    case 'canceled': {
      const getCancelationMessage = () => {
        const cancelAt = subscription.cancelAt ? new Date(subscription.cancelAt) : null;
        const canceledAt = subscription.canceledAt ? new Date(subscription.canceledAt) : null;

        if (canceledAt) {
          const canceledAtDate = formatDateTime(canceledAt, {
            timezone: checkout.timezone,
            locale: theme.locale,
          });
          return `Your subscription was canceled at ${canceledAtDate}.`;
        }

        if (cancelAt) {
          const cancelDate = formatDateTime(cancelAt, {
            timezone: checkout.timezone,
            locale: theme.locale,
          });

          return `Your subscription will be canceled on ${cancelDate}.`;
        }

        return 'Your subscription has been canceled.';
      };

      return (
        <div className="cp_status-wrapper text-center px-4 py-8 w-full">
          <div className="cp_status-content-wrapper">
            <h2 className="cp_status-title mb-2 text-[var(--cp_checkout-confirmation-heading-color)] [font-size:var(--cp_product-title-font-size)] [line-height:var(--cp_product-title-line-height)] [font-family:var(--cp_product-title-font-family)] [font-weight:var(--cp_product-title-font-weight)] [font-style:var(--cp_product-title-font-style)]">
              Subscription canceled
            </h2>
            <div className="cp_status-content mb-4 text-[var(--cp_checkout-confirmation-text-color)]">
              {getCancelationMessage()}
            </div>
          </div>
        </div>
      );
    }
    case 'failed':
    case 'unpaid':
      return (
        <div className="cp_status-wrapper text-center px-4 py-8 w-full">
          <div className="cp_status-content-wrapper">
            <h2 className="cp_status-title mb-2 text-[var(--cp_checkout-confirmation-heading-color)] [font-size:var(--cp_product-title-font-size)] [line-height:var(--cp_product-title-line-height)] [font-family:var(--cp_product-title-font-family)] [font-weight:var(--cp_product-title-font-weight)] [font-style:var(--cp_product-title-font-style)]">
              Payment failed
            </h2>
            <div className="cp_status-content mb-4 text-[var(--cp_checkout-confirmation-text-color)]">
              Your payment failed and has not been processed.
              <br />
              Please try again.
            </div>
          </div>
          <StyledElement
            elementPath="checkout.form.button"
            handleClick={() => restartPayment({ isConfirmation })}
            as={Button}
            appearance="submit"
            className={'cp_button whitespace-nowrap leading-snug'}
            size="full"
            disabled={isDisabled}
          >
            Try again
          </StyledElement>
        </div>
      );
    default:
      return (
        <div className="cp_status-wrapper text-center px-4 py-8 w-full">
          <div className="cp_status-content-wrapper">
            <h2 className="cp_status-title mb-2 text-[var(--cp_checkout-confirmation-heading-color)] [font-size:var(--cp_product-title-font-size)] [line-height:var(--cp_product-title-line-height)] [font-family:var(--cp_product-title-font-family)] [font-weight:var(--cp_product-title-font-weight)] [font-style:var(--cp_product-title-font-style)]">
              Something went wrong
            </h2>
            <div className="cp_status-content mb-4 text-[var(--cp_checkout-confirmation-text-color)]">
              Please contact support or try again.
            </div>
          </div>
          <StyledElement
            elementPath="checkout.form.button"
            handleClick={() => restartPayment({ isConfirmation })}
            as={Button}
            appearance="submit"
            className={'cp_button whitespace-nowrap leading-snug'}
            size="full"
            disabled={isDisabled}
          >
            Try again
          </StyledElement>
        </div>
      );
  }
};

SubscriptionStatusMessage.propTypes = {
  checkout: PropTypes.object,
  subscription: PropTypes.object,
  status: PropTypes.oneOf(['trialing', 'active', 'failed', 'canceled', 'unpaid']),
  restartPayment: PropTypes.func,
};

SubscriptionStatusMessage.defaultProps = {
  checkout: {},
  subscription: {},
  status: 'failed',
  restartPayment: null,
};

export default SubscriptionStatusMessage;
